import React from "react";
import ZoomableDiagram from "../../components/zoomableDiagram/index.jsx";
import "./implementationplan.scss";

// ─── Implementation Plan Window ──────────────────────────────────────────
// Full-width "plan window" card (Claude-Code-in-VSCode / MuleSoft vibe) rendered
// in the center chat column, for the hub phase `type: "implementation_plan_pending"`
// that runs AFTER the pipeline-upload step and JUST BEFORE project generation.
// Shows the rich structured plan (projects → endpoints → flows → mappings →
// operations → errors → tests → config → files, plus decisions/assumptions) and
// the two PlantUML diagrams (apiLed + architecture) inline. The user approves /
// edits in plain English / resolves decisions / asks / undoes before generation.
//
// Props:
//   data            { plan, round, maxRounds, openDecisions, lastEdit, canEdit, editHint, diagrams }
//   fallbackMd      the raw markdown message (used when `plan` is missing/thin)
//   renderMd        renderMdText from the parent (markdown fallback renderer)
//   loading         disables actions while a turn is in flight
//   plantumlEncoder the plantuml-encoder module (for inline diagram rendering)
//   onAction        (text, { noBubble }) => void  — posts prompt to the hub

const C = {
  bg:        "#0e1219",
  panel:     "#121825",
  border:    "rgba(255,255,255,0.08)",
  borderHi:  "rgba(59,127,255,0.30)",
  accent:    "#3b7fff",
  text:      "#dce8ff",
  sub:       "#aac0dc",
  muted:     "#7a93bc",
  faint:     "#3d5273",
  good:      "#00c98d",
  warn:      "#f5a623",
  chip:      "rgba(59,127,255,0.12)",
  hi:        "rgba(0,201,141,0.14)",
};

// Replies that should NOT push a user bubble (approve / undo / show / questions).
function isNoBubble(text) {
  const t = (text || "").trim().toLowerCase();
  return t === "approve" || t === "undo" || /^show\b/.test(t) || t.endsWith("?");
}

function Section({ title, hint, children }) {
  return (
    <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: "0.06em", marginBottom: hint ? 2 : 8, textTransform: "uppercase" }}>
        {title}
      </div>
      {hint && <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
}

function SourceTag({ source }) {
  if (!source) return null;
  const label = {
    you: "you", answered: "you answered", accepted: "accepted default",
    assumed: "assumed by Intelog", prompt: "from your request", resolved: "resolved",
    grounded: "based on your requirements", diagram: "from the diagram",
  }[source] || source;
  const assumed = source === "assumed";
  return (
    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4,
      background: assumed ? "rgba(245,166,35,0.14)" : "rgba(59,127,255,0.12)", color: assumed ? C.warn : C.accent }}>
      {label}
    </span>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${color}22`, color }}>
      {label}
    </span>
  );
}

function ListLine({ id, text, source }) {
  return (
    <div style={{ fontSize: 12, color: C.text, lineHeight: 1.55, padding: "2px 0" }}>
      {id && <span style={{ color: C.faint, fontWeight: 700, marginRight: 6 }}>{id}</span>}{text}
      <SourceTag source={source} />
    </div>
  );
}

// A collapsible PlantUML diagram rendered inline via ZoomableDiagram.
function DiagramBlock({ title, code, plantumlEncoder, defaultOpen }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  if (!code) return null;
  return (
    <div style={{ marginBottom: 10, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", background: C.bg }}>
      <div onClick={() => setOpen((o) => !o)}
        style={{ padding: "9px 12px", background: "rgba(59,127,255,0.05)", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: C.muted, fontSize: 11, width: 12 }}>{open ? "▾" : "▸"}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</span>
      </div>
      {open && (
        <div style={{ padding: 8 }}>
          <ZoomableDiagram plantumlCode={code} plantumlEncoder={plantumlEncoder} onCodeUpdate={() => {}} onSplit={null} />
        </div>
      )}
    </div>
  );
}

// Field-mapping table (from → to / transform / required / source).
function MappingTable({ m }) {
  const dir = { request: "Request → payload", response: "Result → response", backend: "Payload → system", persistence: "Payload → table" }[m.direction] || m.direction;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, marginBottom: 4 }}>
        <span style={{ color: C.faint, marginRight: 6 }}>{m.id}</span>{m.title}
        <span style={{ color: C.muted, fontWeight: 400 }}> · {dir}{m.target ? ` → ${m.target}` : ""}</span>
      </div>
      <div className="ip-table-wrap">
        <table className="ip-table">
          <thead><tr><th>From</th><th>To</th><th>Transform</th><th>Req</th></tr></thead>
          <tbody>
            {(m.rows || []).map((r, i) => (
              <tr key={i}>
                <td>{r.from || "—"}</td><td><code>{r.to}</code></td><td>{r.transform}</td><td>{r.required ? "✓" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FlowCard({ flow, changedIds }) {
  const [open, setOpen] = React.useState(false);
  const steps = flow.steps || [];
  const changed = changedIds.has(String(flow.id));
  return (
    <div style={{ marginBottom: 8, border: `1px solid ${changed ? "rgba(0,201,141,0.35)" : C.border}`, borderRadius: 8, overflow: "hidden", background: C.bg }}>
      <div onClick={() => setOpen((o) => !o)} style={{ padding: "8px 11px", background: "rgba(59,127,255,0.05)", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: C.muted, fontSize: 11, width: 12 }}>{open ? "▾" : "▸"}</span>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, flex: 1 }}>{flow.id} {flow.name}</div>
        <span style={{ fontSize: 10, color: C.faint }}>{steps.length} step{steps.length !== 1 ? "s" : ""}</span>
      </div>
      {open && (
        <div style={{ padding: "6px 8px" }}>
          {flow.title && <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontStyle: "italic" }}>{flow.title}{flow.file ? ` · ${flow.file}` : ""}</div>}
          {steps.map((s) => {
            const isBranch = s.kind === "branch";
            const isNote = s.kind === "note" || s.kind === "effect";
            return (
              <div key={s.id} style={{ display: "flex", gap: 8, fontSize: 12, lineHeight: 1.55, padding: "3px 6px", color: isNote ? C.muted : C.text, fontStyle: isNote ? "italic" : "normal", background: changedIds.has(String(s.id)) ? C.hi : "transparent", borderRadius: 5 }}>
                <span style={{ color: C.faint, fontWeight: 700, minWidth: 42, fontVariantNumeric: "tabular-nums" }}>{s.id}</span>
                <span>
                  <span style={{ color: C.accent, fontWeight: 600, marginRight: 4 }}>{s.kind}</span>
                  {isBranch ? <span style={{ color: C.warn }}>{s.text}</span> : s.text}
                  {s.target ? <span style={{ color: C.sub }}> → {s.target}</span> : null}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// One file group in the project structure (API contract, Flows, Config, …) — a
// collapsible heading + its description + the list of file paths.
function FileGroup({ g, defaultOpen }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const paths = g.paths || [];
  return (
    <div style={{ marginBottom: 6, border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden", background: C.bg }}>
      <div onClick={() => setOpen((o) => !o)} style={{ padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: C.muted, fontSize: 11, width: 12 }}>{open ? "▾" : "▸"}</span>
        <span style={{ color: C.faint, fontWeight: 700, fontSize: 11 }}>{g.id}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text, flex: 1 }}>📁 {g.title}</span>
        {g.control && <span style={{ fontSize: 10, color: C.muted }}>{g.control}</span>}
        <span style={{ fontSize: 10, color: C.faint }}>{paths.length} file{paths.length !== 1 ? "s" : ""}</span>
      </div>
      {open && (
        <div style={{ padding: "4px 10px 8px 30px" }}>
          {g.description && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontStyle: "italic" }}>{g.description}</div>}
          {paths.map((p, i) => (
            <div key={i} style={{ fontSize: 11.5, color: C.sub, lineHeight: 1.6, wordBreak: "break-all" }}>
              <code style={{ background: "rgba(59,127,255,0.10)", padding: "1px 5px", borderRadius: 4 }}>{p}</code>
            </div>
          ))}
          {g.because && <div style={{ fontSize: 10.5, color: C.faint, marginTop: 6 }}><em>{g.because}</em></div>}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ p, changedIds, defaultOpen }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const changed = changedIds.has(String(p.id));
  const endpoints = p.endpoints || [];
  const flows = p.flows || [];
  const mappings = p.mappings || [];
  const counts = [
    (p.operations || []).length && `${p.operations.length} ops`,
    (p.errors || []).length && `${p.errors.length} errors`,
    (p.tests || []).length && `${p.tests.length} tests`,
    (p.config || []).length && `${p.config.length} config`,
  ].filter(Boolean).join(" · ");
  return (
    <div style={{ marginBottom: 10, border: `1px solid ${changed ? "rgba(0,201,141,0.35)" : C.border}`, borderRadius: 8, overflow: "hidden", background: C.bg }}>
      <div onClick={() => setOpen((o) => !o)} style={{ padding: "10px 12px", background: "rgba(59,127,255,0.06)", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: C.muted, fontSize: 11, width: 12 }}>{open ? "▾" : "▸"}</span>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, flex: 1 }}>{p.id}. {p.name}</div>
          {p.layer && <Pill label={p.layer} color={C.accent} />}
        </div>
        {p.purpose && <div style={{ fontSize: 11, color: C.muted, marginTop: 4, marginLeft: 20 }}>{p.purpose}</div>}
      </div>
      {open && (
        <div style={{ padding: "8px 12px" }}>
          {p.trigger && (
            <div style={{ fontSize: 11, color: C.sub, marginBottom: 8 }}>
              <b>Starts on:</b> {p.trigger.chosen}{p.trigger.because ? ` — ${p.trigger.because}` : ""}
            </div>
          )}

          {(p.files || []).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Project structure</div>
              {p.files.map((g, i) => <FileGroup key={g.id} g={g} defaultOpen={i === 0} />)}
            </div>
          )}

          {endpoints.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Endpoints</div>
              <div className="ip-table-wrap">
                <table className="ip-table">
                  <thead><tr><th>Id</th><th>Method</th><th>Path</th><th>Flow</th></tr></thead>
                  <tbody>
                    {endpoints.map((e) => (
                      <tr key={e.id}>
                        <td>{e.id}</td><td>{e.method}</td><td><code>{e.path}</code></td><td>{e.flowName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {flows.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Flows</div>
              {flows.map((f) => <FlowCard key={f.id} flow={f} changedIds={changedIds} />)}
            </div>
          )}

          {mappings.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Field mappings</div>
              {mappings.map((m) => <MappingTable key={m.id} m={m} />)}
            </div>
          )}

          {counts && (
            <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>
              Also planned: {counts} — type <b style={{ color: C.accent }}>show {p.id}</b> for the full detail.
              {Array.isArray(p.calls) && p.calls.length > 0 ? ` · Calls: ${p.calls.join(", ")}` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Controlled: the answer lives in the parent's `decisionAnswers` map so all
// decisions submit together (one "Set answers" button, one request). Enter submits all.
function DecisionInput({ d, value, onChange, onSubmitAll, loading }) {
  const prompt = d.question || d.text || d.name || "";
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
        <span style={{ color: C.warn, fontWeight: 700, marginRight: 6 }}>{d.id}</span>{prompt}
      </div>
      {d.suggestion && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>If you don’t answer: {d.suggestion}</div>}
      <input value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !loading) onSubmitAll(); }}
        placeholder={d.suggestion ? `e.g. ${d.suggestion}` : "your choice…"}
        style={{ width: "100%", boxSizing: "border-box", marginTop: 6, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, outline: "none" }} />
    </div>
  );
}

function ActionBar({ text, setText, sendTyped, act, loading, busy, canEdit, requests }) {
  const disabled = loading || busy;
  const [expanded, setExpanded] = React.useState({});
  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTyped(); } };
  return (
    <div style={{ padding: "12px 16px", position: "sticky", bottom: 0, zIndex: 20, background: C.panel, borderTop: `1px solid ${C.border}` }}>
      {Array.isArray(requests) && requests.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Your requests</div>
          <div style={{ maxHeight: 96, overflowY: "auto" }}>
            {requests.map((r, i) => {
              const long = (r || "").length > 120 || /\n/.test(r || "");
              const open = !!expanded[i];
              return (
                <div key={i} style={{ fontSize: 12, color: C.sub, lineHeight: 1.5, padding: "3px 8px", marginBottom: 3, background: "rgba(59,127,255,0.08)", borderRadius: 6 }}>
                  <span style={{ color: C.muted }}>you:</span>{" "}
                  <span style={long && !open ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", whiteSpace: "pre-wrap" } : { whiteSpace: "pre-wrap" }}>{r}</span>
                  {long && (
                    <button onClick={() => setExpanded((p) => ({ ...p, [i]: !open }))}
                      style={{ display: "block", marginTop: 2, background: "transparent", border: "none", color: C.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0 }}>{open ? "▴ show less" : "▾ show more"}</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button onClick={() => act("approve", { noBubble: true })} disabled={disabled}
          style={{ flex: 1, padding: "9px", borderRadius: 7, background: C.accent, border: "none", color: "#fff", fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}>✅ Approve &amp; generate</button>
        <button onClick={() => act("undo", { noBubble: true })} disabled={disabled}
          style={{ padding: "9px 14px", borderRadius: 7, background: "transparent", border: `1px solid ${C.border}`, color: C.sub, fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}>↶ Undo</button>
      </div>
      {canEdit !== false && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)}
            onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px"; }}
            onKeyDown={onKeyDown} rows={2}
            placeholder="Describe a change, resolve a decision (D1: …), ask a question, or type approve…  (Enter to send · Shift+Enter for newline)"
            disabled={disabled}
            style={{ flex: 1, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", fontSize: 12, outline: "none", resize: "none", minHeight: 38, maxHeight: 140, overflowY: "auto", fontFamily: "inherit", lineHeight: 1.5 }} />
          <button onClick={sendTyped} disabled={disabled || !text.trim()}
            style={{ padding: "8px 14px", borderRadius: 7, border: "none", background: "rgba(59,127,255,0.18)", color: C.accent, fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", opacity: disabled || !text.trim() ? 0.5 : 1 }}>Send</button>
        </div>
      )}
      {disabled && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Working…</div>}
    </div>
  );
}

export default function ImplementationPlanWindow({ data, fallbackMd, renderMd, loading, plantumlEncoder, onAction }) {
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [maximized, setMaximized] = React.useState(false);
  const [requests, setRequests] = React.useState([]);
  // "Please check first" answers, collected across all decisions and submitted in one go.
  const [decisionAnswers, setDecisionAnswers] = React.useState({});

  const rootStyle = maximized
    ? { position: "fixed", inset: 0, zIndex: 1000, margin: 0, borderRadius: 0, maxHeight: "100vh", border: "none", background: C.panel }
    : { marginTop: 12, border: `1px solid ${C.borderHi}`, borderRadius: 10, overflow: "hidden", background: C.panel };

  React.useEffect(() => { setBusy(false); setDecisionAnswers({}); }, [data]);

  const plan = data && data.plan;
  const round = data && data.round;
  const maxRounds = data && data.maxRounds;
  const lastEdit = (data && data.lastEdit) || null;
  const changedIds = new Set((lastEdit && lastEdit.changedIds ? lastEdit.changedIds : []).map(String));
  const diagrams = (data && data.diagrams) || (plan && plan.diagrams) || null;
  // Diagram 1 ("who calls what"): springboot/dotnet = Service architecture (Domain-Driven
  // Microservices); mule = API-led connectivity. Backend sends diagrams.service for the former
  // and keeps diagrams.apiLed for all techs (backward compatibility).
  const tech = (plan && plan.tech) || (data && data.tech) || "mule";
  const primaryDiagramTitle = (tech === "springboot" || tech === "dotnet")
    ? "Service architecture" : "API-led connectivity";
  const primaryDiagramCode = diagrams && (diagrams.service || diagrams.serviceArchitecture || diagrams.apiLed);

  const act = (value, opts) => {
    if (loading || busy) return;
    setBusy(true);
    onAction(value, opts);
  };
  const sendTyped = () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    setRequests((prev) => [...prev, t]);
    act(t, { noBubble: isNoBubble(t) });
  };

  // Fallback: no structured plan — render the markdown message with an action bar.
  if (!plan) {
    return (
      <div className="sh-impl-plan-card" style={{ marginTop: 12, border: `1px solid ${C.borderHi}`, borderRadius: 10, overflow: "hidden", background: C.panel }}>
        {diagrams && (
          <Section title="Diagrams">
            <DiagramBlock title={primaryDiagramTitle} code={primaryDiagramCode} plantumlEncoder={plantumlEncoder} defaultOpen />
            <DiagramBlock title="Runtime architecture" code={diagrams.architecture} plantumlEncoder={plantumlEncoder} />
          </Section>
        )}
        {fallbackMd && <div style={{ padding: "14px 16px" }}>{renderMd ? renderMd(fallbackMd) : fallbackMd}</div>}
        <ActionBar text={text} setText={setText} sendTyped={sendTyped} act={act} loading={loading} busy={busy} canEdit={data && data.canEdit !== false} requests={requests} />
      </div>
    );
  }

  const projects = plan.projects || [];
  const stack = plan.stack || [];
  const needsDecision = (data && data.openDecisions && data.openDecisions.length ? data.openDecisions : plan.needsDecision) || [];

  return (
    <div className="sh-impl-plan-card" style={rootStyle}>

      {/* Header */}
      <div style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, background: "rgba(59,127,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>🛠️ {plan.title || "Implementation Plan"}</div>
            {plan.goal && <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>{plan.goal}</div>}
            {plan.scope && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{plan.scope}</div>}
          </div>
          <button onClick={() => setMaximized((m) => !m)} title={maximized ? "Exit full screen" : "Expand to full screen"}
            style={{ flexShrink: 0, padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.sub, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{maximized ? "⤡ Collapse" : "⤢ Expand"}</button>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          {projects.length > 0 && <Pill label={`${projects.length} projects`} color={C.good} />}
          {needsDecision.length > 0 && <Pill label={`${needsDecision.length} waiting for you`} color={C.accent} />}
          {round != null && maxRounds != null && (
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.faint, fontVariantNumeric: "tabular-nums" }}>edit {round} / {maxRounds}</span>
          )}
        </div>
      </div>

      {/* Last edit feedback */}
      {lastEdit && (lastEdit.applied?.length || lastEdit.refused?.length || lastEdit.removed?.length) ? (
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
          {(lastEdit.applied || []).map((a, i) => (<div key={`a${i}`} style={{ color: C.good }}>✓ {a}</div>))}
          {(lastEdit.removed || []).map((r, i) => (<div key={`r${i}`} style={{ color: C.muted, textDecoration: "line-through" }}>{r}</div>))}
          {(lastEdit.refused || []).map((r, i) => (<div key={`x${i}`} style={{ color: C.warn }}>⚠ {r}</div>))}
        </div>
      ) : null}

      {/* Diagrams (inline) */}
      {diagrams && (primaryDiagramCode || diagrams.architecture) && (
        <Section title="Diagrams">
          <DiagramBlock title={primaryDiagramTitle} code={primaryDiagramCode} plantumlEncoder={plantumlEncoder} defaultOpen />
          <DiagramBlock title="Runtime architecture" code={diagrams.architecture} plantumlEncoder={plantumlEncoder} />
        </Section>
      )}

      {/* Technology stack */}
      {stack.length > 0 && (
        <Section title="Technology stack">
          <div className="ip-table-wrap">
            <table className="ip-table">
              <thead><tr><th>Item</th><th>Version</th><th>Used for</th></tr></thead>
              <tbody>
                {stack.map((s, i) => (<tr key={i}><td><code>{s.item}</code></td><td>{s.value}</td><td>{s.purpose}</td></tr>))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Please check first — fill all answers, submit together in one request */}
      {needsDecision.length > 0 && (() => {
        const filled = Object.keys(decisionAnswers).filter((id) => (decisionAnswers[id] || "").trim());
        const submitDecisions = () => {
          const ops = filled.map((id) => ({ op: "resolve_decision", id, choice: decisionAnswers[id].trim() }));
          if (ops.length && !(loading || busy)) { act({ implementationPlanEdit: { ops } }, { noBubble: false }); setDecisionAnswers({}); }
        };
        return (
          <Section title="Please check first" hint="Fill the ones you want, then set them all at once.">
            {needsDecision.map((d) => (
              <DecisionInput key={d.id} d={d}
                value={decisionAnswers[d.id] || ""}
                onChange={(val) => setDecisionAnswers((p) => ({ ...p, [d.id]: val }))}
                onSubmitAll={submitDecisions}
                loading={loading || busy} />
            ))}
            <button onClick={submitDecisions} disabled={loading || busy || filled.length === 0}
              style={{ marginTop: 2, padding: "7px 16px", borderRadius: 7, border: "none", background: C.accent, color: "#fff", fontSize: 12, fontWeight: 700,
                cursor: (loading || busy || filled.length === 0) ? "default" : "pointer", opacity: (loading || busy || filled.length === 0) ? 0.5 : 1 }}>
              Set answers{filled.length ? ` (${filled.length})` : ""}
            </button>
          </Section>
        );
      })()}

      {/* Projects */}
      {projects.length > 0 && (
        <Section title="Projects">
          {projects.map((p, i) => <ProjectCard key={p.id} p={p} changedIds={changedIds} defaultOpen={i === 0} />)}
        </Section>
      )}

      {/* Cross-cutting */}
      {plan.crossCutting && plan.crossCutting.length > 0 && (
        <Section title="Cross-cutting">
          {plan.crossCutting.map((s) => (<ListLine key={s.id} id={s.id} text={s.topic ? `${s.topic}: ${s.text}` : s.text} />))}
        </Section>
      )}

      {/* Decisions / Assumptions / Added details / Not included */}
      {plan.decisions && plan.decisions.length > 0 && (
        <Section title="Your decisions">
          {plan.decisions.map((d) => (<ListLine key={d.id} id={d.id} text={d.text} source={d.source} />))}
        </Section>
      )}
      {plan.assumptions && plan.assumptions.length > 0 && (
        <Section title="Assumptions">
          {plan.assumptions.map((a) => (<ListLine key={a.id} id={a.id} text={a.text} source={a.source} />))}
        </Section>
      )}
      {plan.addedDetails && plan.addedDetails.length > 0 && (
        <Section title="Details I added that you did not state" hint="Say “keep G1” to accept one, or tell me the right value.">
          {plan.addedDetails.map((g) => (<ListLine key={g.id} id={g.id} text={`${g.label || g.kind} ${g.token}${Array.isArray(g.refs) && g.refs.length ? ` — used in ${g.refs.join(", ")}` : ""}`} />))}
        </Section>
      )}
      {plan.notIncluded && plan.notIncluded.length > 0 && (
        <Section title="Not included">
          {plan.notIncluded.map((x) => (<ListLine key={x.id} id={x.id} text={x.text} />))}
        </Section>
      )}

      <ActionBar text={text} setText={setText} sendTyped={sendTyped} act={act} loading={loading} busy={busy} canEdit={data && data.canEdit !== false} requests={requests} />
    </div>
  );
}
